import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import isString from 'lodash/isString';
import { Icon, Link } from 'xm-spark';

import LibraryIconImage from 'resources/svg/icons/library.svg';
import VideoIconImage from 'resources/svg/icons/video.svg';
import {
  getHelpViewFooterLinkKnowledgeBaseUrl,
  getHelpViewFooterLinkMobileAppUrl,
  getHelpViewFooterLinkSupportUrl,
  getHelpViewFooterLinkVideosUrl,
  isEverbridgeFlowDesignerApp,
} from 'selectors/featureToggles';
import styles from './QuickLinks.module.scss';

function QuickLinksItem({ href, icon, testId, textId }) {
  const isIconImage = !isString(icon);
  return (
    <li className={styles.quickLinkItem}>
      <Link
        className={styles.link}
        data-testid={testId}
        href={href}
        target="_blank"
        rel="noreferrer"
      >
        <Icon type={!isIconImage && icon} className={styles.icon}>
          {isIconImage ? icon : null}
        </Icon>
        <FormattedMessage id={textId}>
          {msg => <span className={styles.text}>{msg}</span>}
        </FormattedMessage>
      </Link>
    </li>
  );
}

QuickLinksItem.displayName = 'QuickLinksItem';
QuickLinksItem.propTypes = {
  href: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  textId: PropTypes.string.isRequired,
};

export default function QuickLinks() {
  // EBFD does not show quick links:
  const isEverbridgeFlowDesigner = useSelector(isEverbridgeFlowDesignerApp);

  // Customizable links, when blank (empty String) the item will not render (COR-70761):
  const knowledgeBaseUrl = useSelector(getHelpViewFooterLinkKnowledgeBaseUrl);
  const mobileAppUrl = useSelector(getHelpViewFooterLinkMobileAppUrl);
  const supportUrl = useSelector(getHelpViewFooterLinkSupportUrl);
  const videosUrl = useSelector(getHelpViewFooterLinkVideosUrl);

  const isEmpty = !(
    knowledgeBaseUrl ||
    mobileAppUrl ||
    supportUrl ||
    videosUrl
  );
  if (isEmpty || isEverbridgeFlowDesigner) {
    return null;
  }

  return (
    <ul className={styles.quickLinks}>
      {videosUrl && (
        <QuickLinksItem
          icon={<VideoIconImage />}
          href={videosUrl}
          testId="HelpView_quickLink_tutorials"
          textId="HELP_MENU_QUICK_LINK_VIDEOS"
        />
      )}
      {supportUrl && (
        <QuickLinksItem
          icon="icon-compose"
          href={supportUrl}
          testId="HelpView_quickLink_submitSupportRequest"
          textId="HELP_MENU_QUICK_LINK_SUBMIT_REQUEST"
        />
      )}
      {knowledgeBaseUrl && (
        <QuickLinksItem
          icon={<LibraryIconImage />}
          href={knowledgeBaseUrl}
          testId="HelpView_quickLink_knowledgeBase"
          textId="HELP_MENU_QUICK_LINK_KNOWLEDGE_BASE"
        />
      )}
      {mobileAppUrl && (
        <QuickLinksItem
          icon="icon-mobile"
          href={mobileAppUrl}
          testId="HelpView_quickLink_mobileApp"
          textId="HELP_MENU_QUICK_LINK_MOBILE_APP"
        />
      )}
    </ul>
  );
}

QuickLinks.displayName = 'QuickLinks';
